/**
 * ArchivedChannelsPanel - Browse + search archived channels.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { chatChannelsApi } from '../../services/chatApi';
import { useChat } from '../../contexts/ChatContext';
import type { Channel } from '../../types/chat';

interface ArchivedChannelsPanelProps {
  onClose: () => void;
}

const ArchivedChannelsPanel: React.FC<ArchivedChannelsPanelProps> = ({ onClose }) => {
  const { t } = useTranslation('chat');
  const { setCurrentChannel, refreshChannels } = useChat();
  const [archived, setArchived] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    chatChannelsApi.listArchived().then(setArchived).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleRestore = useCallback(async (channelId: string) => {
    try {
      await chatChannelsApi.unarchive(channelId);
      setArchived(prev => prev.filter(c => c.id !== channelId));
      await refreshChannels();
    } catch (e) {
      console.error('Failed to unarchive:', e);
    }
  }, [refreshChannels]);

  const handleView = useCallback((channel: Channel) => {
    setCurrentChannel(channel);
    onClose();
  }, [setCurrentChannel, onClose]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-sm">{t('archivedChannels')}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : archived.length > 0 ? (
          archived.map(ch => (
            <div key={ch.id} className="px-4 py-3 border-b flex items-center justify-between">
              <div>
                <div className="text-sm font-medium"># {ch.name}</div>
                {ch.description && (
                  <div className="text-xs text-gray-500 mt-0.5">{ch.description}</div>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleView(ch)}
                  className="text-xs text-blue-600 hover:underline"
                >
                  {t('view')}
                </button>
                <button
                  onClick={() => handleRestore(ch.id)}
                  className="text-xs text-green-600 hover:underline"
                >
                  {t('restore')}
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-gray-400 text-sm py-8">{t('noArchivedChannels')}</div>
        )}
      </div>
    </div>
  );
};

export default ArchivedChannelsPanel;
